/**
 * AddressForm.tsx — a reusable UK address form.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the delivery address is collected at checkout and edited again on the
 * dashboard (both the delivery address and the registered address). One
 * controlled form keeps validation and labelling consistent. Callers own the
 * submit (POST/PATCH) and pass a label + busy flag.
 *
 * DIRTY (navigation resilience): dirty vs the LAST SAVED snapshot — `initial`
 * at mount, then WHATEVER WAS LAST SUBMITTED, so a caller that keeps the form
 * mounted after a save still sees later edits as dirty (a one-shot `saved`
 * flag went silent for exactly those). Guards a real router navigation away
 * (browser Back/tab close/refresh, via `useUnsavedChangesGuard`'s
 * `useBlocker`/`beforeunload`) — this form owns no in-app Back/Cancel button
 * itself in any caller, so there is nothing else to route through
 * `guardedAction` here.
 *
 * ⚠️ THAT GUARD BLOCKS EVERY ROUTER NAVIGATION WHILE DIRTY — including one the
 * caller makes itself. So this form must NEVER be mounted beside a control
 * whose success navigates (2026-09-23 review): the merged checkout step once
 * kept it live under the pay button, and one keystroke during a card confirm
 * made it block `/thank-you` for an order that had just been placed. The
 * checkout now UNMOUNTS it once the address is saved (a read-only "Deliver
 * to" block with a Change control replaces it) and withdraws every pay
 * control while it is open.
 *
 * `onDirtyChange` (optional): tells the CALLER when the fields differ from the
 * last saved snapshot. The checkout uses it only to word the status line under
 * the re-opened form ("You've changed your delivery address…").
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Address } from '../api/types';
import { Button, Field, TextInput } from '../ui/kit';
import type { ButtonVariant } from '../ui/kit';
import { LeaveConfirmDialog } from '../navigation/LeaveConfirmDialog';
import { useUnsavedChangesGuard } from '../navigation/useUnsavedChangesGuard';

export function AddressForm({
  initial,
  submitLabel,
  busy = false,
  idPrefix = 'addr',
  ctaVariant = 'cta',
  submitArrow = false,
  onSubmit,
  onDirtyChange,
}: {
  initial?: Partial<Address>;
  submitLabel: string;
  busy?: boolean;
  idPrefix?: string;
  ctaVariant?: ButtonVariant;
  /** Append the shared ButtonArrow to the submit (checkout's step-forward CTA). */
  submitArrow?: boolean;
  onSubmit: (address: Address) => void;
  /** Fires whenever the fields drift from (or return to) the last SAVED values. See the header. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [line1, setLine1] = useState(initial?.line1 ?? '');
  const [line2, setLine2] = useState(initial?.line2 ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [postcode, setPostcode] = useState(initial?.postcode ?? '');
  const [error, setError] = useState<string | null>(null);
  // The last values handed to onSubmit — RAW field text, not the trimmed
  // output, so "unchanged" is judged against what is literally in the boxes.
  const [lastSaved, setLastSaved] = useState({
    line1: initial?.line1 ?? '',
    line2: initial?.line2 ?? '',
    city: initial?.city ?? '',
    postcode: initial?.postcode ?? '',
  });
  const dirty =
    line1 !== lastSaved.line1 || line2 !== lastSaved.line2 || city !== lastSaved.city || postcode !== lastSaved.postcode;
  const guard = useUnsavedChangesGuard(dirty);
  // The callback rides a ref so the effect below depends on `dirty` alone:
  // callers pass inline functions, and re-running on every render would spam
  // the caller with identical answers (and invite an update loop if the caller
  // sets state from it).
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(() => {
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (!line1.trim() || !city.trim() || postcode.trim().length < 5) {
      setError('Please add the first line, town/city and a valid postcode.');
      return;
    }
    setError(null);
    setLastSaved({ line1, line2, city, postcode });
    onSubmit({
      line1: line1.trim(),
      ...(line2.trim() ? { line2: line2.trim() } : {}),
      city: city.trim(),
      postcode: postcode.trim().toUpperCase(),
    });
  }

  return (
    <form onSubmit={handleSubmit} aria-label="Address" noValidate>
      <LeaveConfirmDialog open={guard.promptOpen} onStay={guard.cancelLeave} onLeave={guard.confirmLeave} />
      <Field label="Address line 1" htmlFor={`${idPrefix}-line1`}>
        <TextInput id={`${idPrefix}-line1`} value={line1} autoComplete="address-line1" onChange={(e) => setLine1(e.target.value)} />
      </Field>
      <Field label="Address line 2 (optional)" htmlFor={`${idPrefix}-line2`}>
        <TextInput id={`${idPrefix}-line2`} value={line2} autoComplete="address-line2" onChange={(e) => setLine2(e.target.value)} />
      </Field>
      <div className="gps-address-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
        <Field label="Town or city" htmlFor={`${idPrefix}-city`}>
          <TextInput id={`${idPrefix}-city`} value={city} autoComplete="address-level2" onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field label="Postcode" htmlFor={`${idPrefix}-postcode`}>
          <TextInput id={`${idPrefix}-postcode`} value={postcode} autoComplete="postal-code" onChange={(e) => setPostcode(e.target.value)} />
        </Field>
      </div>
      {error ? (
        <p role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
          {error}
        </p>
      ) : null}
      <Button type="submit" variant={ctaVariant} disabled={busy} arrow={submitArrow && !busy}>
        {busy ? 'Saving…' : submitLabel}
      </Button>
    </form>
  );
}
